/**
 * Analytics Page — ArgusAI
 * Query volume, token usage, agent performance, model performance.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { BarChart3, Loader2, Activity, Cpu, Users, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { apiClient } from '@/lib/api-client';

export const Route = createFileRoute('/analytics')({
  component: AnalyticsPage,
});

interface Overview {
  totalQueries: number;
  activeUsers: number;
  totalTokens: number;
  avgLatencyMs: number;
}

interface QueryPoint { date: string; count: number; }
interface AgentUsage { agentId: string; count: number; }
interface ModelPerf { model: string; queryCount: number; avgLatencyMs: number; totalTokens: number; }

function AnalyticsPage() {
  const [range, setRange] = useState('7d');
  const [overview, setOverview] = useState<Overview | null>(null);
  const [queryData, setQueryData] = useState<QueryPoint[]>([]);
  const [agentData, setAgentData] = useState<AgentUsage[]>([]);
  const [modelData, setModelData] = useState<ModelPerf[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, [range]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [ov, qd, ad, md] = await Promise.all([
        apiClient.get<Overview>(`/analytics/overview?range=${range}`),
        apiClient.get<{ data: QueryPoint[] }>(`/analytics/queries?range=${range}`),
        apiClient.get<{ data: AgentUsage[] }>(`/analytics/agents?range=${range}`),
        apiClient.get<{ data: ModelPerf[] }>(`/analytics/models?range=${range}`),
      ]);
      setOverview(ov);
      setQueryData(qd.data);
      setAgentData(ad.data);
      setModelData(md.data);
    } catch {}
    setLoading(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BarChart3 className="h-6 w-6" />
          Analytics
        </h1>
        <Tabs value={range} onValueChange={setRange}>
          <TabsList>
            <TabsTrigger value="24h">24h</TabsTrigger>
            <TabsTrigger value="7d">7d</TabsTrigger>
            <TabsTrigger value="30d">30d</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Overview Cards */}
      {overview && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground flex items-center gap-1"><Activity className="h-4 w-4" />Total Queries</CardTitle></CardHeader>
            <CardContent><p className="text-3xl font-bold">{overview.totalQueries.toLocaleString()}</p></CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground flex items-center gap-1"><Users className="h-4 w-4" />Active Users</CardTitle></CardHeader>
            <CardContent><p className="text-3xl font-bold">{overview.activeUsers.toLocaleString()}</p></CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground flex items-center gap-1"><Zap className="h-4 w-4" />Total Tokens</CardTitle></CardHeader>
            <CardContent><p className="text-3xl font-bold">{overview.totalTokens.toLocaleString()}</p></CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground flex items-center gap-1"><Cpu className="h-4 w-4" />Avg Latency</CardTitle></CardHeader>
            <CardContent><p className="text-3xl font-bold">{overview.avgLatencyMs.toLocaleString()}ms</p></CardContent>
          </Card>
        </div>
      )}

      {/* Query Volume */}
      <Card>
        <CardHeader><CardTitle>Query Volume</CardTitle></CardHeader>
        <CardContent>
          {queryData.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No query data yet. Start using Ask Argus!</p>
          ) : (
            <div className="flex items-end gap-1 h-40">
              {queryData.map((d, i) => {
                const max = Math.max(...queryData.map(q => q.count), 1);
                const height = (d.count / max) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-xs text-muted-foreground">{d.count}</span>
                    <div className="w-full bg-primary/70 rounded-t" style={{ height: `${Math.max(height, 2)}%` }} />
                    <span className="text-[10px] text-muted-foreground truncate w-full text-center">
                      {new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4">
        {/* Agent Usage */}
        <Card>
          <CardHeader><CardTitle>Agent Usage</CardTitle></CardHeader>
          <CardContent>
            {agentData.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No agent data yet</p>
            ) : (
              <div className="space-y-3">
                {agentData.sort((a, b) => b.count - a.count).map(a => (
                  <div key={a.agentId} className="flex items-center justify-between">
                    <span className="text-sm font-medium">{a.agentId}</span>
                    <Badge variant="secondary">{a.count} queries</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Model Performance */}
        <Card>
          <CardHeader><CardTitle>Model Performance</CardTitle></CardHeader>
          <CardContent>
            {modelData.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No model data yet</p>
            ) : (
              <div className="space-y-3">
                {modelData.map(m => (
                  <div key={m.model} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{m.model}</p>
                      <p className="text-xs text-muted-foreground">{m.queryCount} queries · {m.avgLatencyMs}ms avg</p>
                    </div>
                    <Badge variant="outline">{m.totalTokens.toLocaleString()} tokens</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
